import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, ScanLine, Download, Save, Eye, Trash2, Plus, X, Mail, User, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TODAY = new Date().toISOString().slice(0, 10);

const EMPTY = {
  verification_date: TODAY,
  owner_name: '',
  owner_address: '',
  owner_phone: '',
  owner_email: '',
  pet_name: '',
  pet_species: 'Dog',
  pet_breed: '',
  pet_sex_status: 'Female Spayed',
  pet_dob: '',
  pet_colour: '',
  microchip_number: '',
  microchip_location: 'Between the shoulder blades',
  microchip_iso_compliant: true,
  microchip_readable: true,
  notes: '',
  verified_by: 'Dr. Samantha Dorman, MSc, BVSc',
  verified_title: 'Veterinarian and Veterinary Travel Consultant',
  license_number: '139',
  accreditation_number: '',
};

export default function MicrochipVerifications() {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password') || '';
  const auth = { 'Authorization': `Basic ${btoa(':' + password)}` };
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null); // null = list view; object = editing

  const fetchList = async () => {
    const r = await fetch(`${API_URL}/api/admin/microchip-verifications`, { headers: auth });
    setList(await r.json());
  };

  useEffect(() => { fetchList(); }, []);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const preview = async () => {
    const r = await fetch(`${API_URL}/api/admin/microchip-verifications/preview-pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...auth },
      body: JSON.stringify(form),
    });
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob), '_blank');
  };

  const save = async () => {
    const isEdit = Boolean(form.id);
    const r = await fetch(
      `${API_URL}/api/admin/microchip-verifications${isEdit ? '/' + form.id : ''}`,
      {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(form),
      },
    );
    if (!r.ok) return alert(`Save failed: HTTP ${r.status}`);
    setForm(null);
    fetchList();
  };

  const download = async (item) => {
    const r = await fetch(`${API_URL}/api/admin/microchip-verifications/${item.id}/pdf`, { headers: auth });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `microchip-verification-${item.certificate_number}.pdf`;
    a.click();
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete certificate ${item.certificate_number}? This cannot be undone.`)) return;
    await fetch(`${API_URL}/api/admin/microchip-verifications/${item.id}`, {
      method: 'DELETE', headers: auth,
    });
    fetchList();
  };

  const emailCert = (item) => {
    // Deep-link into the Email Composer with the certificate pre-attached and
    // — when available — the owner's email pre-filled in the "To" field.
    const params = new URLSearchParams();
    params.set('attach', `microchip_cert:${item.id}`);
    if (item.owner_email) params.set('to', item.owner_email);
    params.set(
      'subject',
      `Microchip Verification Certificate — ${item.pet_name || 'your pet'} (${item.certificate_number})`,
    );
    navigate(`/admin/email-composer?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-gray-500 hover:text-gray-700"><ArrowLeft className="w-5 h-5" /></Link>
            <div>
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <ScanLine className="w-5 h-5 text-emerald-600" /> Microchip Verifications
              </h1>
              <p className="text-xs text-gray-500">Generate signed verification certificates for clients.</p>
            </div>
          </div>
          {!form && (
            <Button onClick={() => setForm({ ...EMPTY })} data-testid="new-microchip-cert-btn">
              <Plus className="w-4 h-4 mr-2" /> New Verification
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {form ? (
          <FormView
            form={form} setField={setField} onPreview={preview}
            onSave={save} onCancel={() => setForm(null)}
            auth={auth}
          />
        ) : (
          <ListView list={list} onEdit={setForm} onDownload={download} onDelete={remove} onEmail={emailCert} />
        )}
      </div>
    </div>
  );
}


function ListView({ list, onEdit, onDownload, onDelete, onEmail }) {
  if (!list.length) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
        No verifications yet. Click <b>New Verification</b> to create your first certificate.
      </div>
    );
  }
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr className="text-left text-xs uppercase text-gray-500">
            <th className="px-4 py-3">Certificate #</th>
            <th className="px-4 py-3">Pet</th>
            <th className="px-4 py-3">Owner</th>
            <th className="px-4 py-3">Chip #</th>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {list.map(it => (
            <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-3 font-mono text-emerald-700 font-semibold">{it.certificate_number}</td>
              <td className="px-4 py-3">
                <div className="font-medium">{it.pet_name}</div>
                <div className="text-xs text-gray-500">{it.pet_species} · {it.pet_breed}</div>
              </td>
              <td className="px-4 py-3">
                <div>{it.owner_name}</div>
                {it.owner_email && <div className="text-xs text-gray-500">{it.owner_email}</div>}
              </td>
              <td className="px-4 py-3 font-mono text-xs">{it.microchip_number}</td>
              <td className="px-4 py-3 text-xs text-gray-500">{it.verification_date}</td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <button
                  onClick={() => onEmail(it)}
                  title={it.owner_email ? `Email to ${it.owner_email}` : 'Email this certificate'}
                  className="p-2 text-teal-700 hover:bg-teal-50 rounded"
                  data-testid={`email-cert-${it.id}`}
                >
                  <Mail className="w-4 h-4" />
                </button>
                <button onClick={() => onDownload(it)} title="Download PDF" className="p-2 text-emerald-700 hover:bg-emerald-50 rounded" data-testid={`download-cert-${it.id}`}>
                  <Download className="w-4 h-4" />
                </button>
                <button onClick={() => onEdit({ ...it })} title="Edit" className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                  <ScanLine className="w-4 h-4" />
                </button>
                <button onClick={() => onDelete(it)} title="Delete" className="p-2 text-red-600 hover:bg-red-50 rounded">
                  <Trash2 className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// Typeahead that searches admin CRM clients and, when picked, autofills the
// owner_name/address/phone/email fields on the form so the vet doesn't retype.
function ClientPicker({ setField, auth }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/admin/clients/search?query=${encodeURIComponent(q)}`,
          { headers: auth },
        );
        setResults(r.ok ? await r.json() : []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const pick = (c) => {
    setField('owner_name', c.name || '');
    setField('owner_address', c.address || '');
    setField('owner_phone', c.phone || '');
    setField('owner_email', c.email || '');
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="col-span-2 relative" ref={boxRef}>
      <label className="text-xs font-semibold text-gray-600 flex items-center gap-1">
        <User className="w-3 h-3" /> Auto-fill from client
      </label>
      <div className="relative mt-1">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          className="border rounded w-full pl-9 pr-3 py-2"
          placeholder="Search clients by name, email or phone…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim().length >= 2 && setOpen(true)}
          data-testid="client-picker-input"
        />
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
          {loading && <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-500">No matching clients — type a bit more, or fill the fields below manually.</div>
          )}
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => pick(c)}
              className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-gray-100 last:border-0"
              data-testid={`client-picker-option-${c.id}`}
            >
              <div className="text-sm font-medium">{c.name}</div>
              <div className="text-xs text-gray-500">
                {[c.email, c.phone].filter(Boolean).join(' · ') || '—'}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function Field({ form, setField, label, k, type = 'text', placeholder, options }) {
  if (options) {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-600">{label}</label>
        <select className="border rounded w-full px-3 py-2 mt-1" value={form[k] ?? ''} onChange={e => setField(k, e.target.value)}>
          {options.map(o => <option key={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (type === 'textarea') {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-600">{label}</label>
        <textarea className="border rounded w-full px-3 py-2 mt-1" rows="3" value={form[k] ?? ''} onChange={e => setField(k, e.target.value)} />
      </div>
    );
  }
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600">{label}</label>
      <input type={type} className="border rounded w-full px-3 py-2 mt-1" placeholder={placeholder} value={form[k] ?? ''} onChange={e => setField(k, e.target.value)} />
    </div>
  );
}

function Checkbox({ form, setField, label, k }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={Boolean(form[k])} onChange={e => setField(k, e.target.checked)} />
      {label}
    </label>
  );
}

function FormView({ form, setField, onPreview, onSave, onCancel, auth }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
      <h2 className="text-lg font-semibold">{form.id ? `Edit ${form.certificate_number}` : 'New Verification'}</h2>

      <section>
        <h3 className="text-sm font-semibold text-emerald-700 mb-3">Certificate</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field form={form} setField={setField} label="Certificate Number" k="certificate_number" placeholder="Leave blank to auto-assign MV-YYYY-NNNN" />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-emerald-700 mb-3">Owner / Client Contact</h3>
        <div className="grid grid-cols-2 gap-3">
          <ClientPicker setField={setField} auth={auth} />
          <Field form={form} setField={setField} label="Owner Name" k="owner_name" placeholder="Jane Doe" />
          <Field form={form} setField={setField} label="Verification Date" k="verification_date" type="date" />
          <div className="col-span-2"><Field form={form} setField={setField} label="Owner Address" k="owner_address" placeholder="123 Main St, George Town" /></div>
          <Field form={form} setField={setField} label="Phone" k="owner_phone" type="tel" placeholder="+1 345 555 0100" />
          <Field form={form} setField={setField} label="Email" k="owner_email" type="email" placeholder="owner@example.com" />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-emerald-700 mb-3">Pet</h3>
        <div className="grid grid-cols-3 gap-3">
          <Field form={form} setField={setField} label="Pet Name" k="pet_name" />
          <Field form={form} setField={setField} label="Species" k="pet_species" options={['Dog', 'Cat', 'Other']} />
          <Field form={form} setField={setField} label="Breed" k="pet_breed" />
          <Field form={form} setField={setField} label="Sex & Status" k="pet_sex_status" options={['Female Spayed', 'Female Intact', 'Male Neutered', 'Male Intact', 'Unknown']} />
          <Field form={form} setField={setField} label="Date of Birth (optional)" k="pet_dob" type="date" />
          <Field form={form} setField={setField} label="Colour / Markings (optional)" k="pet_colour" />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-emerald-700 mb-3">Microchip</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field form={form} setField={setField} label="Microchip Number" k="microchip_number" placeholder="985141001234567" />
          <Field form={form} setField={setField} label="Location on Pet" k="microchip_location" />
          <div className="col-span-2 flex flex-col gap-2">
            <Checkbox form={form} setField={setField} label="Successfully read on scan" k="microchip_readable" />
            <Checkbox form={form} setField={setField} label="ISO 11784/11785 compliant" k="microchip_iso_compliant" />
          </div>
        </div>
        <div className="mt-3">
          <Field form={form} setField={setField} label="Additional Notes (optional)" k="notes" type="textarea" />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-emerald-700 mb-3">Signed By</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field form={form} setField={setField} label="Name" k="verified_by" />
          <Field form={form} setField={setField} label="Title" k="verified_title" />
          <Field form={form} setField={setField} label="License Number" k="license_number" />
          <Field form={form} setField={setField} label="Accreditation Number (optional)" k="accreditation_number" placeholder="Leave blank until received" />
        </div>
        <p className="text-xs text-gray-500 mt-2">A DocuSign-style e-signature block is applied automatically at PDF generation time — a cursive rendering of the signer name plus a timestamp and document-hash audit trail.</p>
      </section>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}><X className="w-4 h-4 mr-2" /> Cancel</Button>
        <Button variant="outline" onClick={onPreview} data-testid="preview-cert-btn"><Eye className="w-4 h-4 mr-2" /> Preview PDF</Button>
        <Button onClick={onSave} data-testid="save-cert-btn"><Save className="w-4 h-4 mr-2" /> Save Certificate</Button>
      </div>
    </div>
  );
}
